/**
 * Composant de gestion des alertes d'échéances de paiement
 * Affiche les échéances à venir et permet de déclencher des alertes manuelles
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Bell, Calendar, Clock, Euro, RefreshCw, Send, AlertTriangle, CheckCircle, XCircle, Mail, Home, Building2, Search, Filter } from 'lucide-react';
import { getToken } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const formatMontant = (montant) => {
  if (!montant && montant !== 0) return 'N/A';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant);
};

export default function AlertesEcheances() {
  const [echeances, setEcheances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDays, setFilterDays] = useState(30);
  const [triggeringCheck, setTriggeringCheck] = useState(false);

  // Charger les données
  const fetchData = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      
      // Charger les échéances et le statut en parallèle
      const [echeancesRes, statusRes] = await Promise.all([
        axios.get(`${API}/echeances/upcoming`, {
          headers: getAuthHeader(),
          params: { days: filterDays }
        }),
        axios.get(`${API}/echeances/status`, { headers: getAuthHeader() })
      ]);
      
      setEcheances(echeancesRes.data?.echeances || []);
      setSchedulerStatus(statusRes.data?.scheduler || null);
      
      if (showToast) {
        toast.success(`${echeancesRes.data?.count || 0} échéance(s) chargée(s)`);
      }
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Déclencher une vérification manuelle
  const handleTriggerCheck = async () => {
    setTriggeringCheck(true);
    try {
      const response = await axios.post(
        `${API}/echeances/trigger-check`,
        {},
        { headers: getAuthHeader() }
      );
      
      const result = response.data?.result || {};
      const totalSent = result.total_sent || 0;
      
      if (totalSent > 0) {
        toast.success(`${totalSent} alerte(s) envoyée(s) avec succès`);
      } else {
        toast.info('Aucune alerte à envoyer pour le moment');
      }
      
      // Recharger les données
      fetchData();
    } catch {
      toast.error('Erreur lors du déclenchement des alertes');
    } finally {
      setTriggeringCheck(false);
    }
  };

  // Filtrer les échéances
  const filteredEcheances = echeances.filter(e => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      e.reference?.toLowerCase().includes(search) ||
      e.nom?.toLowerCase().includes(search) ||
      e.email?.toLowerCase().includes(search)
    );
  });

  // Statistiques
  const stats = {
    total: echeances.length,
    urgent: echeances.filter(e => e.days_until <= 3).length,
    soon: echeances.filter(e => e.days_until > 3 && e.days_until <= 7).length,
    later: echeances.filter(e => e.days_until > 7).length,
    totalMontant: echeances.reduce((sum, e) => sum + (e.montant || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <Bell className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-xl">Alertes Échéances de Paiement</CardTitle>
                <CardDescription className="text-white/80">
                  Gestion automatique des rappels d'échéances (J-7, J-3, J-1)
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fetchData(true)}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleTriggerCheck}
                disabled={triggeringCheck}
                className="bg-white text-orange-600 hover:bg-white/90"
              >
                <Send className={`h-4 w-4 mr-2 ${triggeringCheck ? 'animate-pulse' : ''}`} />
                {triggeringCheck ? 'Envoi...' : 'Envoyer les alertes'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-slate-400" />
              <div>
                <p className="text-3xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-400">Total échéances</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-red-200" />
              <div>
                <p className="text-3xl font-bold">{stats.urgent}</p>
                <p className="text-sm text-red-200">Urgentes (≤3 jours)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-amber-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-amber-200" />
              <div>
                <p className="text-3xl font-bold">{stats.soon}</p>
                <p className="text-sm text-amber-200">Proches (4-7 jours)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Euro className="h-8 w-8 text-emerald-200" />
              <div>
                <p className="text-3xl font-bold">{formatMontant(stats.totalMontant)}</p>
                <p className="text-sm text-emerald-200">Montant total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler Status */}
      {schedulerStatus && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {schedulerStatus.running ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <span className="font-medium">
                  Scheduler {schedulerStatus.running ? 'actif' : 'inactif'}
                </span>
              </div>
              {schedulerStatus.jobs?.[0]?.next_run && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Clock className="h-4 w-4" />
                  Prochaine exécution: {new Date(schedulerStatus.jobs[0].next_run).toLocaleString('fr-FR')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, référence, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <select
                value={filterDays}
                onChange={(e) => setFilterDays(Number(e.target.value))}
                className="border rounded-md px-3 py-2 text-sm"
              >
                <option value={7}>7 prochains jours</option>
                <option value={14}>14 prochains jours</option>
                <option value={30}>30 prochains jours</option>
                <option value={60}>60 prochains jours</option>
                <option value={90}>90 prochains jours</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des échéances */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            Échéances à venir ({filteredEcheances.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredEcheances.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p>Aucune échéance dans les {filterDays} prochains jours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEcheances.map((echeance, index) => (
                <div
                  key={echeance.reference || index}
                  className={`p-4 rounded-lg border ${
                    echeance.days_until <= 1 ? 'bg-red-50 border-red-200' :
                    echeance.days_until <= 3 ? 'bg-amber-50 border-amber-200' :
                    echeance.days_until <= 7 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {echeance.type === 'domium' ? (
                        <Home className="h-5 w-5 text-indigo-500 mt-0.5" />
                      ) : (
                        <Building2 className="h-5 w-5 text-blue-500 mt-0.5" />
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{echeance.nom}</span>
                          <Badge variant="outline" className="text-xs">
                            {echeance.type === 'domium' ? 'DOMIUM' : 'BATICO'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600">{echeance.reference}</p>
                        <p className="text-sm text-slate-500">{echeance.email}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        echeance.days_until <= 1 ? 'text-red-600' :
                        echeance.days_until <= 3 ? 'text-amber-600' :
                        echeance.days_until <= 7 ? 'text-yellow-600' :
                        'text-slate-700'
                      }`}>
                        {echeance.days_until === 0 ? "Aujourd'hui" :
                         echeance.days_until === 1 ? 'Demain' :
                         `J-${echeance.days_until}`}
                      </div>
                      <p className="text-sm text-slate-500">{formatDate(echeance.echeance)}</p>
                      <p className="text-sm font-semibold text-slate-700 mt-1">
                        {formatMontant(echeance.montant)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Indicateurs d'alertes envoyées */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-200">
                    <span className="text-xs text-slate-500">Alertes envoyées:</span>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={echeance.alerts_sent?.j7 ? 'default' : 'outline'}
                        className={`text-xs ${echeance.alerts_sent?.j7 ? 'bg-green-500' : ''}`}
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        J-7
                      </Badge>
                      <Badge 
                        variant={echeance.alerts_sent?.j3 ? 'default' : 'outline'}
                        className={`text-xs ${echeance.alerts_sent?.j3 ? 'bg-green-500' : ''}`}
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        J-3
                      </Badge>
                      <Badge 
                        variant={echeance.alerts_sent?.j1 ? 'default' : 'outline'}
                        className={`text-xs ${echeance.alerts_sent?.j1 ? 'bg-green-500' : ''}`}
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        J-1
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
